import { useStore } from 'effector-react'
import { ReactNode, useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { uploadPhoto } from '~/features/DocumentBasedRegistration/helpers'
import {
  FixButtonWrapper,
  FormWrapper,
} from '~/features/DocumentBasedRegistration/styled'
import { AggregatorOccupation, Region } from '~/shared/api'
import {
  infoSourceOptions,
  taxSystemEnumOptions,
} from '~/shared/config/constants'
import { AggregatorOccupationCodeEnum } from '~/shared/config/enums'
import { featureFlagModel } from '~/shared/lib/featureFlag'
import { phoneMaskOptions, snilsMaskOptions } from '~/shared/lib/maskito'
import { useTaxiparkQueryParams } from '~/shared/lib/useTaxiparkQueryParams'
import { getObject } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/Buttons'
import {
  FormProvider,
  MaskInput,
  AsyncSelect,
  TextInput,
  PickPhotos,
  Select,
  SelectMultiple,
  DaDataInput,
} from '~/shared/ui/form'
import { FormValues } from './model'

type FormProps = {
  onSuccess: (formValues: FormValues) => void
  form: UseFormReturn<FormValues>
  disabledChangePhone?: boolean
  disabled: boolean
  extraBlock: ReactNode | null
}

export function Form({
  onSuccess,
  form,
  disabledChangePhone,
  disabled,
  extraBlock,
}: FormProps) {
  const { taxiparkIdQuery } = useTaxiparkQueryParams()

  const featureFlags = useStore(featureFlagModel.$featureFlags)

  const { watch, resetField, setValue } = form

  const aggregatorOccupationOption = watch('aggregatorOccupationOption')
  const aggregatorOccupationCode = getObject(aggregatorOccupationOption)?.code

  const isIndividualEntrepreneurRus =
    aggregatorOccupationCode ===
    AggregatorOccupationCodeEnum.individual_entrepreneur_rus

  useEffect(() => {
    setValue('isIndividualEntrepreneur', isIndividualEntrepreneurRus)
  }, [isIndividualEntrepreneurRus, setValue])

  useEffect(() => {
    if (!isIndividualEntrepreneurRus) {
      resetField('taxSystem')
      resetField('inn')
      resetField('email')
    }
  }, [isIndividualEntrepreneurRus, resetField])

  return (
    <FormProvider form={form} onSuccess={onSuccess}>
      <FormWrapper>
        <AsyncSelect
          name='aggregatorOccupationOption'
          label='Тип занятости'
          modalTitle='Тип занятости'
          fetchOptions={AggregatorOccupation.fetchOptions}
        />

        {isIndividualEntrepreneurRus && (
          <>
            <Select
              options={taxSystemEnumOptions}
              name='taxSystem'
              label='Система налогообложения ип'
              modalTitle='Система налогообложения ип'
            />

            <TextInput name='inn' label='ИНН' />

            <TextInput name='email' label='Email' />
          </>
        )}

        <MaskInput
          maskitoOptions={phoneMaskOptions}
          name='phone'
          label='Телефон'
          disabled={disabledChangePhone}
        />
        <AsyncSelect
          name='regionCode'
          label='Регион работы'
          modalTitle='Регион работы'
          fetchOptions={() => Region.fetchOptions(taxiparkIdQuery)}
        />
        <MaskInput
          maskitoOptions={snilsMaskOptions}
          name='snils'
          label='СНИЛС'
        />

        <SelectMultiple
          field='infoSource'
          label='Откуда вы о нас узнали'
          modalTitle='Откуда вы о нас узнали'
          options={infoSourceOptions}
          name='infoSource'
        />

        {featureFlags.address_suggestions_dadata ? (
          <DaDataInput name='registrationAddress' label='Адрес регистрации' />
        ) : (
          <TextInput name='registrationAddress' label='Адрес регистрации' />
        )}

        {featureFlags.address_suggestions_dadata ? (
          <DaDataInput
            name='placeOfResidence'
            label='Фактический адрес проживания'
          />
        ) : (
          <TextInput
            name='placeOfResidence'
            label='Фактический адрес проживания'
          />
        )}

        {featureFlags.address_suggestions_dadata ? (
          <DaDataInput name='birthPlace' label='Место рождения' />
        ) : (
          <TextInput name='birthPlace' label='Место рождения' />
        )}

        <PickPhotos
          descriptions='Добавьте свою фотографию, для персональной идентификации при выдаче автомобиля'
          name='photo'
          fnUpload={(file: Blob) => uploadPhoto(file, taxiparkIdQuery)}
          maxPhotos={1}
        />

        {extraBlock}

        <FixButtonWrapper>
          <Button
            variant='limeFlooded'
            size='large'
            type='submit'
            fullWidth
            data-test-id='basic-step-button'
            disabled={disabled}
          >
            Продолжить
          </Button>
        </FixButtonWrapper>
      </FormWrapper>
    </FormProvider>
  )
}
